import { DRILLS } from '../game/drills';
import { mwLabel } from '../game/scoring';
import type { DayResult, DrillResult, Progress, RunRecord } from '../game/types';
import { streakIsLive } from '../state/progress';
import { must } from './dom';
import { str } from './strings';
import { PERFECT_TIER } from './tiers';

/**
 * One reading: a dim uppercase label and a mono value, optionally with its
 * unit set smaller beside it (STYLEGUIDE §3, §7). The records block is a
 * readout in the stat row's own idiom, never a chart.
 */
export interface Reading {
  label: string;
  value: string;
  unit?: string;
  /** Symbol id drawn before the label, from the one stroke set (§6). */
  icon?: string;
}

export interface RecordsView {
  /**
   * The readings the page leads with. The first sets a step larger than the
   * ones beside it — the hierarchy the strip gives output (STYLEGUIDE §7).
   */
  hero: Reading[];
  /** Readings grouped into lines. A line with nothing to say is dropped. */
  lines: Reading[][];
  /** Stands in for the readings when nothing has been completed yet. */
  empty?: string;
}

/** Wire length below the reference by more than rounding counts as beating it. */
const BEAT_EPSILON = 1e-6;

function median(xs: number[]): number | null {
  if (!xs.length) return null;
  const s = [...xs].sort((a, b) => a - b);
  const mid = s.length >> 1;
  return s.length % 2 ? s[mid] : Math.round((s[mid - 1] + s[mid]) / 2);
}

/**
 * Everything the archive's records block shows, derived from what is already
 * persisted. Nothing here is stored on its own account: this is a reading of
 * the save file, not a second copy of it.
 *
 * `results` holds archive completions alongside dailies, which is right for a
 * count of grids finished and wrong for anything that describes a run of days
 * — the streak is read from the counter the game keeps, never re-derived here.
 */
export function buildRecordsView(
  progress: Progress,
  drills: Record<string, DrillResult>,
  today: number,
): RecordsView {
  const results: DayResult[] = Object.values(progress.results);
  const runs: RunRecord[] = Object.values(progress.runs);

  if (!results.length) {
    return { hero: [], lines: [], empty: str().recordsEmpty };
  }

  const streak = streakIsLive(progress, today) ? progress.streak : 0;
  const best = progress.bestStreak;

  const perfect = results.filter((r) => r.tier === PERFECT_TIER).length;
  const flawless = runs.filter((r) => r.flawless).length;
  const studied = runs.filter((r) => r.studied).length;
  // Only days that stored the wire they were ranked against can answer this.
  // The rest are silent rather than counted as losses (Pillar 4).
  const beat = results.filter((r) => r.refWire != null && r.wire < r.refWire - BEAT_EPSILON).length;

  const mws = results.map((r) => r.mw);
  const tunable = results.filter((r) => r.firstMW != null);
  const tuned = tunable.filter((r) => r.mw > r.firstMW!).length;
  const done = DRILLS.filter((d) => drills[d.id]).length;

  // The page leads with the habit the game is inviting, the way the strip
  // leads with output: the streak in the large slot beside its flame, and
  // the two facts that qualify it — the run it is chasing, and the yard.
  // Never a zero, and never a dead streak: the HUD's rule, applied here.
  const hero: Reading[] = [];
  if (streak > 0) hero.push({ label: str().recStreak, value: String(streak), icon: 'iconFlame' });
  if (best != null && best > 0) hero.push({ label: str().recBestRun, value: String(best) });
  if (done) hero.push({ label: str().recDrills, value: `${done}/${DRILLS.length}` });

  const lines: Reading[][] = [];

  const tally: Reading[] = [{ label: str().recGrids, value: String(results.length) }];
  if (perfect) tally.push({ label: str().recPerfect, value: String(perfect) });
  if (flawless) tally.push({ label: str().recFlawless, value: String(flawless) });
  if (beat) tally.push({ label: str().recBeatEngineers, value: String(beat) });
  lines.push(tally);

  // The median is the one MW reading a page about the player can carry: a
  // best says which grid was generous rather than how the player did, and a
  // median cold read states the tuning reading's own fact a second time.
  const scores: Reading[] = [];
  const mid = median(mws);
  if (mid != null) scores.push({ label: str().recMedian, value: mwLabel(mid), unit: str().unitMW });
  if (tunable.length) {
    scores.push({
      label: str().recTunedPast,
      value: `${Math.round((tuned / tunable.length) * 100)}%`,
    });
  }
  if (studied) scores.push({ label: str().recStudied, value: String(studied) });
  if (scores.length) lines.push(scores);

  return { hero, lines };
}

/** A lead cell: the icon beside a label-over-value column, the strip's shape. */
function heroCell(r: Reading): string {
  return (
    `<span class="hero">` +
    (r.icon ? `<svg class="icon-glyph" aria-hidden="true"><use href="#${r.icon}" /></svg>` : '') +
    `<span class="hero-txt"><i>${r.label}</i> <b>${r.value}</b></span>` +
    `</span>`
  );
}

/** One reading in a line: dim label, mono value, its unit set small beside. */
function reading(r: Reading): string {
  return (
    `<span class="reading"><i>${r.label}</i> <b>${r.value}</b>` +
    (r.unit ? ` <i>${r.unit}</i>` : '') +
    `</span>`
  );
}

/** Render the records block at the head of the profile. */
export function renderRecords(view: RecordsView): void {
  const host = must('profileRecords');
  if (view.empty) {
    host.innerHTML = `<p class="records-empty">${view.empty}</p>`;
    return;
  }
  const hero = view.hero.length
    ? `<div class="hero-row">${view.hero.map(heroCell).join('')}</div>`
    : '';
  host.innerHTML =
    hero +
    view.lines
      .map((line) => `<span class="records-line">${line.map(reading).join('')}</span>`)
      .join('');
}
